import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/currencyUtils';
import { getLocalizedValue } from '../../constants/Languages';
import MembershipPlanService from '../../services/membershipPlanService';
import StyledSwitch from '../common/StyledSwitch';

const actionButtonSx = {
  width: { xs: 40, sm: 32 },
  height: { xs: 40, sm: 32 },
};

const MEMBERSHIP_ENTITLEMENT_TYPE = {
  UnlimitedUse: 'UnlimitedUse',
  FixedAllowancePerPeriod: 'FixedAllowancePerPeriod',
};

const isPlanActive = (status) => MembershipPlanService.isMembershipPlanActive(status);

const MembershipPlanCard = ({
  plan,
  currency,
  loading,
  canEdit,
  onEdit,
  onToggleStatus,
}) => {
  const { t, i18n } = useTranslation();
  const localizedName = getLocalizedValue(plan.name, i18n.language, undefined, t('membershipPlansManagement.card.unnamed'));
  const active = isPlanActive(plan.status);
  const isUnlimited = plan.entitlementType === MEMBERSHIP_ENTITLEMENT_TYPE.UnlimitedUse;

  const renderPlanActions = () => (
    canEdit ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
      <Tooltip title={t('membershipPlansManagement.actions.edit')}>
        <IconButton size="small" onClick={onEdit} disabled={loading} sx={{ ...actionButtonSx, color: '#1976d2' }}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={active
        ? t('membershipPlansManagement.actions.deactivate')
        : t('membershipPlansManagement.actions.activate')}>
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: { xs: 40, sm: 34 } }}>
          <StyledSwitch
            checked={active}
            onChange={onToggleStatus}
            disabled={loading}
            inputProps={{
              'aria-label': active
                ? t('membershipPlansManagement.actions.deactivate')
                : t('membershipPlansManagement.actions.activate'),
            }}
          />
        </Box>
      </Tooltip>
    </Box>
    ) : null
  );

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'flex-start',
        width: '100%',
        transition: 'all 0.3s ease',
        opacity: active ? 1 : 0.85,
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1.3, wordBreak: 'break-word' }}>
            {localizedName}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {isUnlimited
              ? t('membershipPlansManagement.entitlement.unlimited')
              : t('membershipPlansManagement.entitlement.allowance', { count: plan.allowanceQuantity })}
          </Typography>
        </Box>

        <Box sx={{ mt: 1.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: canEdit ? 0.75 : 0 }}>
            <Chip
              size="small"
              label={t('membershipPlansManagement.billingInterval.monthly')}
              sx={{ backgroundColor: '#e3f2fd', color: '#1565c0' }}
            />
            <Chip
              size="small"
              label={active
                ? t('membershipPlansManagement.status.active')
                : t('membershipPlansManagement.status.inactive')}
              sx={{
                backgroundColor: active ? '#e8f5e9' : '#ffebee',
                color: active ? '#2e7d32' : '#c62828',
              }}
            />
            {plan.allowCustomerCancellation ? (
              <Chip
                size="small"
                label={t('membershipPlansManagement.card.customerCancellation')}
                sx={{ backgroundColor: '#fff3e0', color: '#ef6c00' }}
              />
            ) : null}
        </Box>

        {canEdit ? (
          <Box sx={{ display: 'flex' }}>
            {renderPlanActions()}
          </Box>
        ) : null}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 2,
            pt: 1.5,
            mt: 1.5,
            borderTop: '1px solid #e0e0e0',
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('membershipPlansManagement.card.price')}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
              {formatCurrency(plan.price, currency || plan.currency)}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                {t('membershipPlansManagement.card.perMonth')}
              </Typography>
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">
              {t('membershipPlansManagement.card.subscribers')}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {plan.activeSubscriberCount ?? 0}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MembershipPlanCard;
